import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import styled from 'styled-components';
import CustomTable, { Column } from './CustomTable';
import moment from 'moment';
import { OrderInterface } from '../types';
import apiClient from '../services/apiClient';
import EmptyState from './EmptyState';
import OrderIcon from '../assets/svgs/OrderIcon';
import ReactGA from 'react-ga4';
import { getCurrency } from '../services/tableHelper';
import { formatHumanReadableDate } from '../utils/dateUtils';
import { useSelector } from 'react-redux';

const StatusIndicator = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const StatusItem = styled.div<{ color: string }>`
	display: flex;
	align-items: center;
	font-size: 14px;
	color: ${(props) => props.color};
`;

const StatusDot = styled.div<{ color: string }>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${(props) => props.color};
	margin-right: 8px;
`;

interface StorefrontOrdersProps {
	onOrderClick: (order: OrderInterface) => void;
}

export interface StorefrontOrdersRef {
	refreshOrders: () => void;
}

const StorefrontOrders = forwardRef<StorefrontOrdersRef, StorefrontOrdersProps>(({ onOrderClick }, ref) => {
	const [orders, setOrders] = React.useState<OrderInterface[]>([]);
	const [fetchState, setFetchState] = React.useState<'loading' | 'success' | 'failed'>('loading');

	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/orders/storefront',
			title: 'Storefront Orders',
		});
		fetchStorefrontOrders();
	}, []);

	// Expose refresh function to parent component
	useImperativeHandle(ref, () => ({
		refreshOrders: fetchStorefrontOrders,
	}));

	const fetchStorefrontOrders = () => {
		setFetchState('loading');
		apiClient.orders
			.getStorefrontOrders()
			.then((response: any) => {
				setFetchState('success');
				setOrders(response.data);
				ReactGA.event({
					category: 'Storefront Orders',
					action: 'Fetched Orders',
					label: 'Fetch Success',
				});
			})
			.catch((error) => {
				console.error('Error fetching storefront orders:', error);
				setFetchState('failed');
				ReactGA.event({
					category: 'Storefront Orders',
					action: 'Fetch Orders Failed',
					label: 'Fetch Failed',
				});
			});
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'pending_approval':
				return '#f59e0b';
			case 'accepted':
				return '#10b981';
			case 'rejected':
				return '#ef4444';
			case 'processing':
				return '#3b82f6';
			case 'dispatched':
				return '#6366f1';
			case 'completed':
				return '#10b981';
			default:
				return '#6b7280';
		}
	};

	const getStatusLabel = (status: string) => {
		switch (status) {
			case 'pending_approval':
				return 'Pending Approval';
			case 'accepted':
				return 'Accepted';
			case 'rejected':
				return 'Rejected';
			case 'processing':
				return 'Processing';
			case 'dispatched':
				return 'Dispatched';
			case 'completed':
				return 'Completed';
			default:
				return status;
		}
	};

	const columns: Column[] = [
		{
			field: 'date_created',
			headerName: 'Date Ordered',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => {
				const date = params.row.backdated_order_date || params.row.date_created;
				return moment(date).format('YYYY-MM-DD');
			},
			valueGetter: (params) => {
				return params.row.backdated_order_date || params.row.date_created;
			},
			flex: 1,
		},
		{
			field: 'customer_name',
			headerName: 'Customer',
			minWidth: 150,
			headerClassName: 'table-header',
			flex: 1,
		},
		{
			field: 'customer_email',
			headerName: 'Email',
			minWidth: 200,
			headerClassName: 'table-header',
			flex: 1,
		},
		{
			field: 'total_price',
			headerName: 'Total',
			minWidth: 120,
			headerClassName: 'table-header',
			valueGetter: (params) => `${currency} ${Number(params.value || 0).toFixed(2)}`,
			flex: 1,
		},
		{
			field: 'scheduled_date',
			headerName: 'Scheduled For',
			minWidth: 180,
			headerClassName: 'table-header',
			cellClassName: 'text-blue-600',
			renderCell: (params) => {
				if (!params.value) return '-';
				return formatHumanReadableDate(params.value);
			},
			flex: 1,
		},
		{
			field: 'status',
			headerName: 'Status',
			minWidth: 140,
			headerClassName: 'table-header',
			renderCell: (params) => (
				<StatusIndicator>
					<StatusItem color={getStatusColor(params.value)}>
						<StatusDot color={getStatusColor(params.value)} />
						{getStatusLabel(params.value)}
					</StatusItem>
				</StatusIndicator>
			),
			flex: 1,
		},
		{
			field: 'is_order_paid',
			headerName: 'Payment Status',
			minWidth: 130,
			headerClassName: 'table-header',
			renderCell: (params) => (
				<StatusIndicator>
					<StatusItem color={params.value ? '#4ade80' : '#f59e0b'}>
						<StatusDot color={params.value ? '#4ade80' : '#f59e0b'} />
						{params.value ? 'Paid' : 'Pending Payment'}
					</StatusItem>
				</StatusIndicator>
			),
			flex: 1,
		},
		{
			field: 'date_updated',
			headerName: 'Last Updated',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => moment(params.value).format('Do MMM YYYY'),
			flex: 1,
		},
	];

	const handleRowClick = (param: { row: any }) => {
		const order = param.row;
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Storefront Order Row',
			label: `Order ID: ${order.id}`,
		});
		onOrderClick(order);
	};

	return (
		<CustomTable
			columns={columns}
			objects={orders}
			emptyStateComponent={() => (
				<EmptyState
					title="No Storefront Orders Yet"
					description="When customers place orders through your storefront, they'll appear here for you to review and approve or reject."
					icon={<OrderIcon width={50} height={50} />}
				/>
			)}
			fetchState={fetchState}
			sx={{ marginTop: '0' }}
			onRowClick={(param) => handleRowClick(param)}
			initialRowsPerPage={25}
			sortConfig={{ key: 'date_created', direction: 'desc' }}
		/>
	);
});

export default StorefrontOrders;
